import { useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { downloadFile } from '../api/client'
import { useInstruments, useServiceVisit, useServiceVisitActions, useSetVisitInstrument } from '../api/queries'
import type { ServiceItemResult, ServiceVisitDetail, ServiceVisitItem } from '../api/types'
import { servicePhotoLabels } from '../api/types'
import { SignaturePad } from '../components/SignaturePad'
import { SiteContactFields } from '../components/SiteContact'
import { Card, ErrorBox, Loading } from '../components/ui'
import { prepareDocumentFile } from '../lib/documentPhoto'
import { formatDate, formatDateTime } from '../lib/format'
import { useAuthedBlob } from '../lib/photos'

/**
 * Udførelsen af et servicebesøg: checklisten, de to fotos og underskriften.
 *
 * Skærmen er bygget til en telefon i en hånd - store trykflader, ét punkt ad
 * gangen synligt, og hvert svar gemmes med det samme, så et afbrudt besøg kan
 * tages op igen (docs/DECISIONS.md beslutning 026).
 */
export function ServiceVisitPage() {
  const { id } = useParams()
  const visitId = Number(id)
  const navigate = useNavigate()

  const { data, isLoading, error } = useServiceVisit(visitId)
  const actions = useServiceVisitActions(visitId)

  const [notes, setNotes] = useState<string | null>(null)
  const [signature, setSignature] = useState<string | null>(null)
  const [signedByName, setSignedByName] = useState('')
  const [nextDate, setNextDate] = useState('')
  const [completeError, setCompleteError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return <ErrorBox error={new Error('Servicebesøget findes ikke.')} />

  const { visit } = data
  const done = visit.status === 'completed'
  const cancelled = visit.status === 'cancelled'

  const answered = data.items.filter(isAnswered).length
  const missing = data.items.filter((item) => item.isRequired && !isAnswered(item))
  const deviations = data.items.filter((item) => item.result === 'deviation')

  const hasDevicePhoto = data.photos.some((p) => p.kind === 'device')
  const hasLabelPhoto = data.photos.some((p) => p.kind === 'label')

  async function complete() {
    setCompleteError(null)

    if (!signature) {
      setCompleteError('Skriv under i feltet, før besøget kan afsluttes.')
      return
    }

    try {
      await actions.complete.mutateAsync({
        signedByName,
        signatureDataUrl: signature,
        nextServiceDate: nextDate ? new Date(nextDate).toISOString() : null
      })
    } catch (err) {
      setCompleteError(err instanceof Error ? err.message : 'Besøget kunne ikke afsluttes.')
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card>
        <div className="row row--between" style={{ flexWrap: 'wrap', gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <h1 className="page-title" style={{ marginBottom: 2 }}>
              Service · {visit.deviceName}
            </h1>
            <div className="tiny muted">
              <Link to={`/devices/${visit.deviceId}`}>{visit.deviceCode}</Link>
              {visit.checklistName ? ` · ${visit.checklistName}` : ''} · startet{' '}
              {formatDateTime(visit.startedAt)}
            </div>
          </div>

          <StatusPill status={visit.status} deviations={deviations.length} />
        </div>

        {data.site && (
          <div className="form-grid" style={{ marginTop: 14 }}>
            <SiteContactFields contact={data.site} />
          </div>
        )}
      </Card>

      {cancelled && (
        <div className="error-box">
          Besøget er afbrudt. Start et nyt fra enhedens side, hvis servicen skal udføres alligevel.
        </div>
      )}

      {/* Instrumentet vaelges FOER der maales - ellers opdager man foerst ved
          godkendelsen, at maalingerne ikke kan bruges. */}
      <InstrumentCard visit={data} disabled={done || cancelled} />

      <Card title={`Checkliste (${answered} af ${data.items.length})`}>
        {data.items.length === 0 ? (
          <div className="muted small">
            Der er ingen checkliste knyttet til besøget. En administrator kan oprette en under
            Procedurer → Servicechecklister.
          </div>
        ) : (
          <div className="stack" style={{ gap: 14 }}>
            {data.items.map((item) => (
              <ChecklistRow
                key={item.id}
                item={item}
                disabled={done || cancelled}
                onAnswer={(body) => actions.answerItem.mutate({ itemId: item.id, ...body })}
              />
            ))}
          </div>
        )}
      </Card>

      <Card title="Billeder">
        <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
          {servicePhotoLabels.map((entry) => (
            <PhotoSlot
              key={entry.kind}
              visitId={visitId}
              label={entry.label}
              hint={entry.hint}
              photo={data.photos.find((p) => p.kind === entry.kind)}
              disabled={done || cancelled}
              onPick={async (file) => {
                const prepared = await prepareDocumentFile(file)
                await actions.addPhoto.mutateAsync({
                  file: prepared.blob,
                  fileName: prepared.fileName,
                  kind: entry.kind
                })
              }}
            />
          ))}
        </div>
        <div className="tiny muted" style={{ marginTop: 10 }}>
          Billederne gemmes i Service-kanalen i Teams, i enhedens egen mappe.
        </div>
      </Card>

      <Card title="Bemærkning">
        {done ? (
          <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
            {data.notes || <span className="muted">Ingen bemærkning</span>}
          </div>
        ) : (
          <>
            <textarea
              placeholder="Noget der bør følges op på?"
              value={notes ?? data.notes ?? ''}
              onChange={(event) => setNotes(event.target.value)}
              onBlur={() => notes !== null && actions.saveNotes.mutate(notes)}
            />
            <div className="tiny muted">Gemmes automatisk</div>
          </>
        )}
      </Card>

      {done ? (
        <Card title="Underskrevet">
          <div className="row" style={{ flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            <SignatureImage visitId={visitId} />
            <div>
              <div className="small">
                <strong>{data.signedByName}</strong>
              </div>
              <div className="tiny muted">{formatDateTime(data.signedAt)}</div>
              {visit.nextServiceDate && (
                <div className="small" style={{ marginTop: 8 }}>
                  Næste service: {formatDate(visit.nextServiceDate)}
                </div>
              )}
            </div>
          </div>

          {/* Godkendelsen kan kun udstedes, når alt er i orden. Er der afvigelser,
              siger vi hvorfor frem for at skjule knappen - ellers ligner det en fejl. */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            {deviations.length === 0 ? (
              <>
                <button
                  className="btn btn--primary"
                  disabled={downloading}
                  onClick={async () => {
                    setDownloading(true)
                    setCompleteError(null)
                    try {
                      await downloadFile(
                        `/api/service-visits/${visitId}/certificate`,
                        `Godkendelse ${visit.deviceName}.pdf`
                      )
                    } catch (err) {
                      setCompleteError(
                        err instanceof Error ? err.message : 'Godkendelsen kunne ikke hentes.'
                      )
                    } finally {
                      setDownloading(false)
                    }
                  }}
                >
                  {downloading ? 'Henter ...' : 'Hent godkendelse (PDF)'}
                </button>
                {completeError && (
                  <div className="error-box" style={{ marginTop: 10 }}>{completeError}</div>
                )}
                <div className="tiny muted" style={{ marginTop: 6 }}>
                  Erklærer anlægget i forskriftsmæssig stand. Med logo, kundens navn, dato og underskrift.
                </div>
              </>
            ) : (
              <div className="small muted">
                <strong>Ingen godkendelse.</strong>{' '}
                {deviations.length === 1
                  ? 'Der er én afvigelse på eftersynet, så anlægget kan ikke godkendes.'
                  : `Der er ${deviations.length} afvigelser på eftersynet, så anlægget kan ikke godkendes.`}{' '}
                Udbedr forholdet og udfør et nyt eftersyn.
              </div>
            )}
          </div>
        </Card>
      ) : cancelled ? null : (
        <Card title="Afslut og skriv under">
          {missing.length > 0 && (
            <div className="error-box" style={{ marginBottom: 12, background: '#FFF7ED', borderColor: '#FED7AA', color: '#9A3412' }}>
              {missing.length === 1
                ? `Punktet "${missing[0].text}" mangler.`
                : `${missing.length} punkter mangler at blive udfyldt.`}
            </div>
          )}

          {(!hasDevicePhoto || !hasLabelPhoto) && (
            <div className="error-box" style={{ marginBottom: 12, background: '#FFF7ED', borderColor: '#FED7AA', color: '#9A3412' }}>
              {!hasDevicePhoto && !hasLabelPhoto
                ? 'Begge billeder mangler.'
                : !hasDevicePhoto
                  ? 'Foto af enheden mangler.'
                  : 'Foto af servicemærkatet mangler.'}
            </div>
          )}

          <div className="form-grid">
            <label className="field">
              <span className="field__label">Udført af *</span>
              <input
                required
                placeholder="Dit navn"
                value={signedByName}
                onChange={(event) => setSignedByName(event.target.value)}
              />
            </label>

            <label className="field">
              <span className="field__label">Næste service</span>
              <input type="date" value={nextDate} onChange={(event) => setNextDate(event.target.value)} />
              <span className="tiny muted">Tom = regnes ud fra enhedens interval</span>
            </label>
          </div>

          {/* IKKE en <label>: en label sender et klik videre til sin første knap, og
              det var "Ryd" - så underskriften blev slettet, i samme øjeblik fingeren
              blev løftet. */}
          <div className="field">
            <span className="field__label">Underskrift *</span>
            <SignaturePad onChange={setSignature} />
          </div>

          {completeError && <div className="error-box" style={{ marginBottom: 12 }}>{completeError}</div>}

          <div className="btn-group">
            <button
              className="btn btn--primary"
              disabled={actions.complete.isPending || !signedByName.trim() || !signature}
              onClick={complete}
            >
              {actions.complete.isPending ? 'Afslutter ...' : 'Afslut service'}
            </button>
            <button
              className="btn"
              disabled={actions.cancel.isPending}
              onClick={() => {
                if (window.confirm('Afbryd besøget? Det kan ikke tages op igen.')) {
                  actions.cancel.mutate(undefined, {
                    onSuccess: () => navigate(`/devices/${visit.deviceId}`)
                  })
                }
              }}
            >
              Afbryd besøg
            </button>
          </div>
        </Card>
      )}
    </div>
  )
}

/** Ét punkt i checklisten. Måling og afkrydsning ser forskellige ud. */
function ChecklistRow({
  item,
  disabled,
  onAnswer
}: {
  item: ServiceVisitItem
  disabled: boolean
  onAnswer: (body: { result?: ServiceItemResult | null; value?: number | null; note?: string | null }) => void
}) {
  const [note, setNote] = useState(item.note ?? '')
  const [value, setValue] = useState(item.value !== null && item.value !== undefined ? String(item.value) : '')

  const outOfRange =
    item.itemType === 'measurement' &&
    item.value !== null &&
    item.value !== undefined &&
    ((item.minValue !== null && item.minValue !== undefined && item.value < item.minValue) ||
      (item.maxValue !== null && item.maxValue !== undefined && item.value > item.maxValue))

  function saveValue() {
    onAnswer({ value: value === "" ? null : Number(value), note: note || null })
  }

  const range =
    item.minValue !== null && item.minValue !== undefined && item.maxValue !== null && item.maxValue !== undefined
      ? `${item.minValue}-${item.maxValue} ${item.unit ?? ''}`
      : item.minValue !== null && item.minValue !== undefined
        ? `min. ${item.minValue} ${item.unit ?? ''}`
        : item.maxValue !== null && item.maxValue !== undefined
          ? `maks. ${item.maxValue} ${item.unit ?? ''}`
          : null

  return (
    <div className="check-row">
      <div className="check-row__text">
        {item.text}
        {!item.isRequired && <span className="tiny muted"> (valgfri)</span>}
      </div>
      {item.helpText && <div className="tiny muted">{item.helpText}</div>}

      {item.itemType === 'measurement' ? (
        <div className="row" style={{ flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
          <input
            type="number"
            inputMode="decimal"
            step="any"
            style={{ maxWidth: 120 }}
            placeholder="Værdi"
            value={value}
            disabled={disabled}
            onChange={(event) => setValue(event.target.value)}
            onBlur={() => saveValue()}
            // Ogsaa gem paa Enter: paa iOS lukker "Faerdig" ikke altid feltet, og en
            // maaling der kun staar paa skaermen er ikke gemt.
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault()
                event.currentTarget.blur()
                saveValue()
              }
            }}
          />
          <span className="small muted">{item.unit}</span>
          {range && <span className="tiny muted">Forventet {range}</span>}
          {outOfRange && (
            <span className="pill" style={{ background: '#FEE2E2', color: '#991B1B' }}>
              Uden for interval
            </span>
          )}
        </div>
      ) : (
        <div className="row" style={{ gap: 8, marginTop: 6 }}>
          {(
            [
              { value: 'ok', label: 'I orden', background: '#DCFCE7', color: '#166534' },
              { value: 'deviation', label: 'Afvigelse', background: '#FEE2E2', color: '#991B1B' },
              { value: 'na', label: 'Ikke relevant', background: '#F1F5F9', color: '#334155' }
            ] as const
          ).map((option) => (
            <button
              key={option.value}
              type="button"
              className={`check-btn ${item.result === option.value ? 'check-btn--on' : ''}`}
              style={
                item.result === option.value
                  ? { background: option.background, color: option.color, borderColor: option.color }
                  : undefined
              }
              disabled={disabled}
              onClick={() => onAnswer({ result: option.value, note: note || null })}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {/* Bemærkningen er altid tilgængelig - en afvigelse uden forklaring er ubrugelig. */}
      {(item.result === 'deviation' || outOfRange || note) && (
        <input
          style={{ marginTop: 6 }}
          placeholder="Hvad er der galt?"
          value={note}
          disabled={disabled}
          onChange={(event) => setNote(event.target.value)}
          onBlur={() => onAnswer({ result: item.result, value: item.value, note: note || null })}
        />
      )}
    </div>
  )
}

/** Én af de to billedpladser. Viser miniature når billedet er taget. */
function PhotoSlot({
  visitId,
  label,
  hint,
  photo,
  disabled,
  onPick
}: {
  visitId: number
  label: string
  hint: string
  photo?: { id: number; name: string }
  disabled: boolean
  onPick: (file: File) => Promise<void>
}) {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const preview = useAuthedBlob(
    photo ? `/api/service-visits/${visitId}/photos/${photo.id}/content` : ''
  )

  return (
    <div className="photo-slot">
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={async (event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (!file) return

          setBusy(true)
          setError(null)
          try {
            await onPick(file)
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Billedet kunne ikke gemmes.')
          } finally {
            setBusy(false)
          }
        }}
      />

      <button
        type="button"
        className="photo-slot__box"
        disabled={disabled || busy}
        onClick={() => input.current?.click()}
      >
        {preview ? (
          <img src={preview} alt={label} />
        ) : (
          <span className="photo-slot__placeholder">{busy ? 'Gemmer ...' : photo ? '✓' : '📷'}</span>
        )}
      </button>

      <div className="tiny" style={{ marginTop: 4 }}>
        <strong>{label}</strong>
      </div>
      <div className="tiny muted">{photo ? 'Tryk for at tage om' : hint}</div>
      {error && <div className="tiny" style={{ color: 'var(--danger, #991B1B)' }}>{error}</div>}
    </div>
  )
}

/** Den gemte underskrift på et afsluttet besøg. */
function SignatureImage({ visitId }: { visitId: number }) {
  const url = useAuthedBlob(`/api/service-visits/${visitId}/signature`)

  return url ? (
    <img className="signature-image" src={url} alt="Underskrift" />
  ) : (
    <div className="signature-image" />
  )
}

function StatusPill({ status, deviations }: { status: string; deviations: number }) {
  if (status === 'completed') {
    return deviations > 0 ? (
      <span className="pill" style={{ background: '#FEF3C7', color: '#92400E' }}>
        Afsluttet · {deviations} afvigelse{deviations === 1 ? '' : 'r'}
      </span>
    ) : (
      <span className="pill" style={{ background: '#DCFCE7', color: '#166534' }}>
        Afsluttet uden afvigelser
      </span>
    )
  }

  if (status === 'cancelled') {
    return (
      <span className="pill" style={{ background: '#F1F5F9', color: '#64748B' }}>
        Afbrudt
      </span>
    )
  }

  return (
    <span className="pill" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
      I gang
    </span>
  )
}

/** Samme regel som i backenden: en måling tæller som besvaret, når der står en værdi. */
function isAnswered(item: ServiceVisitItem) {
  return item.itemType === 'measurement'
    ? (item.value !== null && item.value !== undefined) || item.result === 'na'
    : Boolean(item.result)
}

/**
 * Måleinstrumentet på besøget (beslutning 047).
 *
 * Vises altid, også når checklisten ikke måler noget - montøren skal kunne se,
 * at der ikke er valgt et, før han står med en rapport, der ikke kan godkendes.
 *
 * Et udløbet instrument kan godt vælges: man står i marken og skal kunne skrive
 * ned, hvad man faktisk målte med. Til gengæld siges det tydeligt, hvad det
 * betyder.
 */
function InstrumentCard({ visit, disabled }: { visit: ServiceVisitDetail; disabled: boolean }) {
  const { data: instruments } = useInstruments()
  const setInstrument = useSetVisitInstrument(visit.visit.id)
  const [error, setError] = useState<string | null>(null)

  // Måler checklisten overhovedet noget? Er svaret nej, er instrumentet frivilligt.
  const measures = visit.items.some((i) => i.itemType === 'measurement')

  async function choose(value: string) {
    setError(null)
    try {
      await setInstrument.mutateAsync(value ? Number(value) : null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Instrumentet kunne ikke vælges.')
    }
  }

  return (
    <Card title="Målt med">
      {!measures && (
        <div className="tiny muted" style={{ marginBottom: 8 }}>
          Checklisten har ingen målepunkter, så et instrument er ikke nødvendigt her.
        </div>
      )}

      {disabled ? (
        <div className="small">
          {visit.instrumentName ? (
            <>
              <strong>{visit.instrumentName}</strong>
              {visit.instrumentSerialNumber ? ` · ${visit.instrumentSerialNumber}` : ''}
            </>
          ) : (
            <span className="muted">Der blev ikke valgt et instrument.</span>
          )}
        </div>
      ) : (
        <label className="field" style={{ maxWidth: 420 }}>
          <span className="field__label">Instrument{measures ? ' *' : ''}</span>
          <select
            value={visit.instrumentId ?? ''}
            disabled={setInstrument.isPending}
            onChange={(e) => choose(e.target.value)}
          >
            <option value="">Intet valgt</option>
            {instruments
              ?.filter((i) => i.isActive || i.id === visit.instrumentId)
              .map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                  {i.serialNumber ? ` (${i.serialNumber})` : ''}
                  {i.isExpired ? ' - kalibrering udløbet' : ''}
                </option>
              ))}
          </select>
        </label>
      )}

      {error && <div className="error-box" style={{ marginTop: 8 }}>{error}</div>}

      {/* Kalibreringen er hele pointen: uden den kan måleværdierne ikke bruges. */}
      {visit.instrumentName && (
        <div className="small" style={{ marginTop: 8 }}>
          {visit.instrumentWasValid === false ? (
            <span className="overdue">
              Instrumentets kalibrering var ikke gyldig{' '}
              {visit.instrumentCalibratedOn
                ? `(udløb ${formatShortDate(visit.instrumentCalibrationValidUntil)})`
                : '(aldrig kalibreret)'}
              . Eftersynet kan udføres, men det kan ikke godkendes - måleværdierne kan ikke
              lægges til grund.
            </span>
          ) : (
            <span className="muted">
              Kalibreret {formatShortDate(visit.instrumentCalibratedOn)}
              {visit.instrumentCalibrationValidUntil
                ? `, gyldig til ${formatShortDate(visit.instrumentCalibrationValidUntil)}`
                : ''}
            </span>
          )}
        </div>
      )}

      {measures && !visit.instrumentName && !disabled && (
        <div className="tiny muted" style={{ marginTop: 8 }}>
          Der måles på denne checkliste. Vælg instrumentet, ellers kan eftersynet ikke godkendes.
        </div>
      )}
    </Card>
  )
}

function formatShortDate(value?: string | null) {
  if (!value) return '-'
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
}
